import { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useInfiniteQuery } from '@tanstack/react-query';
import { apiClient } from '@/lib/api';
import { useAuthStore } from '@store/authStore';
import { ROUTES } from '@/constants';
import { Skeleton } from '@components/ui/Skeleton';
import ClientCard from './components/ClientCard';
import { computeClientSpend } from './utils';
import { EmptyState } from '@assets/images';
import { usePermissions } from '@/hooks/usePermissions';
import { MODULES, SCOPES, ACTIONS } from '@/lib/permissions';

import {
  RiSearchLine,
  RiFilter3Line,
  RiArrowUpDownLine,
  RiUserAddLine,
  RiArrowDownLine,
  RiDownload2Line,
} from '@remixicon/react';

const CLIENTS_PAGE_SIZE = 10;

const Clients = () => {
  const navigate = useNavigate();
  const { userProfile } = useAuthStore();
  const [search, setSearch] = useState('');
  // How many already-loaded clients to render. Load More reveals the next
  // slice from the in-memory list — all pages are fetched up front so the
  // sort is correct from the very first visible card.
  const [visibleCount, setVisibleCount] = useState(CLIENTS_PAGE_SIZE);

  const profileId = userProfile?.userprofile_id;
  const orgId = userProfile?.org_id;

  // Per-role data scope from the permission matrix.
  // self → broker_id: profileId (broker sees own clients)
  // global → broker_org_id: orgId (admin/FC/finance see all clients in the org)
  const { scope: getScope, can, canAccess } = usePermissions();
  const clientsScope = getScope(MODULES.CLIENTS);
  const canAddClient = can(MODULES.CLIENTS, ACTIONS.CREATE);
  // Export is admin-only — gated via the Admin module (admin + super_admin).
  const canExport = canAccess(MODULES.ADMIN);
  const scopeFilter = useMemo(() => {
    if (clientsScope === SCOPES.GLOBAL && orgId) return { broker_org_id: orgId };
    if (clientsScope === SCOPES.SELF && profileId) return { broker_id: profileId };
    return null;
  }, [clientsScope, orgId, profileId]);

  const {
    data: clientsPages,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['clients', clientsScope, profileId, orgId],
    queryFn: async ({ pageParam = 0 }) => {
      const data = await apiClient.getClients(pageParam, CLIENTS_PAGE_SIZE, {
        include: 'quotes,quotes.bids',
        ...scopeFilter,
      });
      return Array.isArray(data) ? data : [];
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const lastCount = Array.isArray(lastPage) ? lastPage.length : 0;
      if (lastCount < CLIENTS_PAGE_SIZE) return undefined;
      return allPages.length * CLIENTS_PAGE_SIZE;
    },
    enabled: !!scopeFilter,
    staleTime: 30000,
  });

  const clients = useMemo(() => clientsPages?.pages?.flat() || [], [clientsPages]);

  // Sort + search run client-side, so we cascade every page on mount.
  // This keeps Load More working (as in-memory slicing) and guarantees
  // the visible cards are in the right order from the start.
  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  // Keep skeletons up until the cascade finishes so the user never sees
  // the mid-load reorder as quote-having clients arrive on later pages.
  const isSettling = isLoading || isFetchingNextPage || hasNextPage;

  const handleExport = () => {
    if (!filteredClients.length) return;
    const csvText = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const headers = ['First Name', 'Last Name', 'Company', 'Email', 'Phone', 'Lifetime Value', 'Average Flight Spend'];
    const rows = filteredClients.map((c) => {
      const { lifetimeValue, avgFlightSpend } = computeClientSpend(c);
      return [
        csvText(c.first_name),
        csvText(c.last_name),
        csvText(c.company_name || c.company),
        csvText(c.email),
        csvText(c.contact_numbers?.[0] || c.phone),
        lifetimeValue,
        avgFlightSpend,
      ];
    });
    const csvContent = [headers, ...rows].map((row) => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `clients-${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const filteredClients = useMemo(() => {
    let result = [...clients];
    if (search.trim()) {
      const q = search.toLowerCase().trim();
      result = result.filter((c) =>
        c.first_name?.toLowerCase().includes(q) ||
        c.last_name?.toLowerCase().includes(q) ||
        c.company_name?.toLowerCase().includes(q) ||
        c.company?.toLowerCase().includes(q) ||
        c.email?.toLowerCase().includes(q)
      );
    }
    // Sort: clients with quotes first, then by latest quote date descending
    result.sort((a, b) => {
      const aHasQuotes = a.quotes?.length > 0 ? 1 : 0;
      const bHasQuotes = b.quotes?.length > 0 ? 1 : 0;
      if (aHasQuotes !== bHasQuotes) return bHasQuotes - aHasQuotes;
      if (aHasQuotes && bHasQuotes) {
        const aLatest = Math.max(...a.quotes.map((q) => new Date(q.created_at).getTime()));
        const bLatest = Math.max(...b.quotes.map((q) => new Date(q.created_at).getTime()));
        return bLatest - aLatest;
      }
      return 0;
    });
    return result;
  }, [clients, search]);

  return (
    <div className="pb-24 sm:pb-8 max-h-[calc(100vh-12rem)] overflow-y-auto pr-2 [&::-webkit-scrollbar]:w-[6px] [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-gray-200 [&::-webkit-scrollbar-thumb]:rounded-full hover:[&::-webkit-scrollbar-thumb]:bg-gray-300 bg-white/50 backdrop-blur-sm rounded-[2rem] p-4 md:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <h1 className="text-2xl font-bold text-gray-900">
          {isLoading ? '' : `${filteredClients.length} `}Clients
        </h1>

        <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
          {/* Search */}
          <div className="relative flex-1 sm:flex-none">
            <RiSearchLine className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 pr-4 py-2 w-full sm:w-68 rounded-full text-sm focus:outline-none"
            />
          </div>

          {/* Filter */}
          <button className="flex items-center gap-1.5 px-3 py-2 rounded-full bg-white text-sm text-gray-700 transition-colors">
            <RiFilter3Line className="w-4 h-4" />
            <span className="hidden sm:inline">Filter</span>
          </button>

          {/* Sort by */}
          <button className="flex items-center gap-1.5 px-3 py-2 rounded-full bg-white text-sm text-gray-700 transition-colors">
            <RiArrowUpDownLine className="w-4 h-4" />
            <span className="hidden sm:inline">Sort by</span>
          </button>

          {/* Add client — requires Clients module create access (hidden for FC, finance, compliance_qa) */}
          {canAddClient && (
            <button
              onClick={() => navigate(ROUTES.ADD_CLIENT.path)}
              className="flex items-center gap-1.5 px-3 py-2 bg-white text-gray-700 rounded-full text-sm font-medium transition-colors"
            >
              <RiUserAddLine className="w-4 h-4" />
              <span className="hidden sm:inline">Add client</span>
            </button>
          )}

          {/* Export currently filtered/sorted clients to CSV — admin only */}
          {canExport && (
            <button
              onClick={handleExport}
              disabled={!filteredClients.length}
              className="flex items-center gap-1.5 px-3 py-2 bg-white text-gray-700 rounded-full text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <RiDownload2Line className="w-4 h-4" />
              <span className="hidden sm:inline">Export</span>
            </button>
          )}
        </div>
      </div>

      {/* Loading */}
      {isSettling && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="bg-white rounded-xl border border-gray-200 p-4 space-y-3">
              <Skeleton className="h-5 w-40" />
              <Skeleton className="h-4 w-60" />
              <Skeleton className="h-4 w-48" />
              <Skeleton className="h-8 w-full" />
            </div>
          ))}
        </div>
      )}

      {/* Empty state */}
      {!isSettling && filteredClients.length === 0 && (
        <div className="text-center py-16">
          <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <img src={EmptyState} alt="No clients" className="w-30 h-30 mx-auto" />
          </div>
          <h3 className="text-md font-semibold text-gray-900 mb-2">
            {search ? 'No clients found' : 'No clients enrolled yet'}
          </h3>
          <p className="text-sm text-gray-500 mb-4">
            {search ? 'Try adjusting your search query.' : ''}
          </p>
          {!search && (
            <button
              onClick={() => navigate(ROUTES.ADD_CLIENT.path)}
              className="inline-flex items-center gap-1.5 px-4 py-2 !bg-white !border border-primary-500 text-primary-500 rounded-full text-sm font-medium transition-colors hover:bg-gray-800"
            >
              <RiUserAddLine className="w-4 h-4" />
              Add Client
            </button>
          )}
        </div>
      )}

      {/* Client cards grid */}
      {!isSettling && filteredClients.length > 0 && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {filteredClients.slice(0, visibleCount).map((client) => (
              <ClientCard
                key={client.client_id || client.id}
                client={client}
              />
            ))}
          </div>
          {visibleCount < filteredClients.length && (
            <div className="flex justify-center mt-8">
              <button
                type="button"
                onClick={() => setVisibleCount((c) => c + CLIENTS_PAGE_SIZE)}
                className="group relative inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm shadow-sm transition-all duration-200 bg-primary-600 text-white hover:bg-primary-700 hover:shadow-lg hover:-translate-y-0.5 active:translate-y-0 active:shadow-sm"
              >
                <span>Load More</span>
                <RiArrowDownLine className="w-4 h-4 transition-transform group-hover:translate-y-0.5" />
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Clients;
